import React, {FC, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';

import {
  globalWindow,
  IframeFeaturePoliciesEnum,
} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {addQueryToUrl} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {FrameViewerState} from '../../../../srcCommon/inboxes/side/sideFrameActions';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {teammateToPluginJSON} from '../../../helpers/pluginHelpers';
import {
  onTrackIntegrationMouseEvent,
  SidePanelFrameMouseTrackingValuesEnum,
  SidePanelTrackingPayload,
} from '../../../helpers/sidePanelTrackingHelpers';
import {IntegrationPluginModel} from '../../../models/api/integrationModel';
import {PluginModel} from '../../../models/api/pluginModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {Dispatch} from '../../../store/appStore';
import {getIsUserGlobalAdmin, getUser} from '../../../store/data/teammates/teammatesSelectors';
import {ViewerPanelId} from '../../../store/panels/panelStateBase';
import {Iframe} from '../../common/iframe';
import {useFrameOnMessage, useWasVisibleOnce} from './sideFrameHooks';

/*
 * Props.
 */

export interface PluginFrameContainerProps {
  panelId: ViewerPanelId;
  viewerState?: FrameViewerState;
  trackingPayload?: SidePanelTrackingPayload;
}

interface PluginFrameContainerComponentProps extends PluginFrameContainerProps {
  dispatch: Dispatch;
  user: TeammateModel;
  isUserGlobalAdmin: boolean;
  plugin: PluginModel | IntegrationPluginModel;
  isVisible?: boolean;
  onRequestWidthChange: HandlerOf<number>;
}

/*
 * Component.
 */

const PluginFrameContainerComponent: FC<PluginFrameContainerComponentProps> = (props) => {
  /*
   * Plugin communication.
   */

  const {user, isUserGlobalAdmin, plugin, isVisible, trackingPayload} = props;
  const [isReady, setIsReady] = useState(false);
  const {isConnected, onMessage, sendDataToIframe, dialogs} = useFrameOnMessage({
    ...props,
    isReady,
    targetOrigin: plugin.domain,
  });

  /*
   * Send data once connected.
   */

  // User.
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    sendDataToIframe({
      type: 'user',
      user: teammateToPluginJSON(user, isUserGlobalAdmin),
    });

    setIsReady(true);
  }, [isConnected, user, isUserGlobalAdmin, sendDataToIframe]);

  // IsVisible.
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    sendDataToIframe({type: 'panel_visible', visible: Boolean(isVisible)});
  }, [isConnected, isVisible, sendDataToIframe]);

  /*
   * Tracking.
   */

  const onTrackMouse = (type: SidePanelFrameMouseTrackingValuesEnum) => {
    if (!trackingPayload) {
      return;
    }
    onTrackIntegrationMouseEvent(type, plugin.name, trackingPayload);
  };

  /*
   * Render.
   */

  const frameEndpoint = useMemo(() => buildPluginIframeEndpoint(plugin), [plugin]);

  // No need to render the iFrame if we weren't visible at least once.
  const wasVisibleOnce = useWasVisibleOnce(props.isVisible);
  if (!wasVisibleOnce) {
    return null;
  }

  return (
    <>
      {dialogs}
      <Iframe
        isVisible={isVisible}
        endpoint={frameEndpoint}
        title={plugin.name}
        onMessage={onMessage}
        onMouseEnter={() => onTrackMouse(SidePanelFrameMouseTrackingValuesEnum.MOUSE_ENTER)}
        onMouseLeave={() => onTrackMouse(SidePanelFrameMouseTrackingValuesEnum.MOUSE_LEAVE)}
        featurePolicies={[IframeFeaturePoliciesEnum.CAMERA, IframeFeaturePoliciesEnum.MICROPHONE]}
      />
    </>
  );
};

function buildPluginIframeEndpoint(plugin: PluginModel | IntegrationPluginModel) {
  try {
    return addQueryToUrl(plugin.endpoint, {
      auth_secret: plugin.authSecret,
      front_domain: globalWindow.location.origin,
    });
  } catch (error) {
    // This shouldn't happen.
    reportToBugsnag(error);
    return plugin.endpoint;
  }
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  user: getUser,
  isUserGlobalAdmin: getIsUserGlobalAdmin,
}));

export const PluginFrameContainer = connect(mapStateToProps)(PluginFrameContainerComponent);
